import type { DragEndEvent } from "@dnd-kit/core";
import { useCallback, useEffect, useRef, useState } from "react";
import { Fragment } from "react/jsx-runtime";
import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { BookingStatus, Status } from "@repo/database";
import {
  DEFAULT_BOOKING_DURATION,
  getTextColor,
  isBetween,
  nearestToMinute,
} from "@repo/utils";
import { createFileRoute } from "@tanstack/react-router";
import {
  addDays,
  addMinutes,
  differenceInMinutes,
  differenceInSeconds,
  format,
  isSameDay,
  set,
} from "date-fns";
import { AlertCircle, ArrowRightIcon, Star } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useShallow } from "zustand/react/shallow";

import type { Booking, PartialBooking } from "~/providers/booking-provider";
import type { Shift } from "~/providers/shift-provider";
import type { Table } from "~/providers/table-provider";
import LabelIcon from "~/components/icons/label-icon";
import { Button } from "~/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import { useBooking } from "~/hooks/use-booking";
import { useMediaQuery } from "~/hooks/use-media-query";
import { useSection } from "~/hooks/use-section";
import { useShift } from "~/hooks/use-shift";
import { useTable } from "~/hooks/use-table";
import { cn, getMatchedShifts } from "~/lib/utils";
import { useBookingManager } from "~/managers/use-booking-manager";
import { useTableManager } from "~/managers/use-table-manager";
import { useTempBookingStore } from "~/stores/use-booking-store";

export const Route = createFileRoute("/_main/bookings/_layout/table-planning")({
  component: TablePlanning,
});

const checkIsOverlapping = (
  booking: PartialBooking,
  existingBookings: PartialBooking[],
): boolean => {
  return existingBookings
    .filter(
      (existingBooking) =>
        existingBooking.id !== booking.id &&
        existingBooking.bookingStatus !== BookingStatus.no_show &&
        existingBooking.bookingStatus !== BookingStatus.canceled &&
        existingBooking.bookingStatus !== BookingStatus.checked_out,
    )
    .some((existingBooking) => {
      const newStart = new Date(booking.startTime);
      const newEnd = new Date(booking.endTime);
      const existingStart = new Date(existingBooking.startTime);
      const existingEnd = new Date(existingBooking.endTime);

      return (
        // New booking starts during existing booking
        (newStart >= existingStart && newStart < existingEnd) ||
        // New booking ends during existing booking
        (newEnd > existingStart && newEnd <= existingEnd) ||
        // New booking encompasses existing booking
        (newStart <= existingStart && newEnd >= existingEnd)
      );
    });
};

const generateTimeslots = (shifts: Shift[], selectedDate: Date) => {
  // Prepare the start and end times for the day based on shifts
  const interval = shifts.reduce(
    (acc, curr) => Math.max(acc, curr.timeslotInterval),
    0,
  );
  const dayStart = set(selectedDate, {
    hours: 7,
    minutes: 0,
    seconds: 0,
    milliseconds: 0,
  });

  const dayEnd = addDays(
    set(selectedDate, {
      hours: 2,
      minutes: 0,
      seconds: 0,
      milliseconds: 0,
    }),
    1,
  );

  const diff = differenceInMinutes(dayEnd, dayStart);

  const timeslots: {
    time: Date;
    capacity: number;
    spots: number;
    interval: number;
  }[] = [];

  for (let i = 0; i <= diff; i += 15) {
    const timeslotTime = addMinutes(dayStart, i);
    timeslots.push({
      time: timeslotTime,
      capacity: 0,
      spots: 0,
      interval,
    });
  }

  // Update timeslots based on shifts
  shifts.forEach((shift) => {
    const shiftStart = set(selectedDate, {
      hours: shift.startTime.getHours(),
      minutes: shift.startTime.getMinutes(),
      seconds: 0,
    });

    const shiftEnd = addMinutes(
      set(selectedDate, {
        hours: shift.endTime.getHours(),
        minutes: shift.endTime.getMinutes(),
        seconds: 0,
      }),
      shift.standardDuration,
    );

    shift.timeslots.forEach((shiftTimeslot) => {
      const shiftSlotTime = set(selectedDate, {
        hours: shiftTimeslot.start.getHours(),
        minutes: shiftTimeslot.start.getMinutes(),
        seconds: 0,
        milliseconds: 0,
      });

      const timeslot = timeslots.find(
        (t) => t.time.getTime() === shiftSlotTime.getTime(),
      );

      if (timeslot) {
        timeslot.capacity = shift.capacity;
        timeslot.spots += shiftTimeslot.spots;
      }
    });

    // Ensure all timeslots within the shift period have the correct capacity
    timeslots.forEach((timeslot) => {
      if (timeslot.time >= shiftStart && timeslot.time < shiftEnd) {
        timeslot.capacity = shift.capacity;
      }
    });
  });

  return timeslots;
};

const getValidCombinations = (
  neighbors: Table[],
  remainingSeats: number,
): Table[][] => {
  const result: Table[][] = [];

  const findCombinations = (
    index: number,
    currentCombo: Table[],
    currentSeats: number,
  ) => {
    if (currentSeats >= remainingSeats) {
      result.push(currentCombo);
      return;
    }

    for (let i = index; i < neighbors.length; i++) {
      findCombinations(
        i + 1,
        [...currentCombo, neighbors[i]],
        currentSeats + neighbors[i].maxSeats,
      );
    }
  };

  findCombinations(0, [], 0);
  return result;
};

function TableBooking({
  booking,

  posX,
  onClick,
}: {
  booking: PartialBooking;
  posX: number;
  onClick: () => void;
}) {
  const { setNodeRef, transform, listeners, attributes } = useDraggable({
    id: booking.id ?? "TEMP",
    disabled: booking.groupSize === 0,
  });

  const { bookings } = useBooking();

  const isOverlapping = checkIsOverlapping(
    booking,
    bookings.filter(
      (b) =>
        b.id !== booking.id &&
        b.tableIds.some((tableId) => booking.tableIds?.includes(tableId)),
    ),
  );

  const { t } = useTranslation("common");

  const { selectedDate, selectedBooking } = useTempBookingStore();

  const { shifts } = useShift();

  const { tables } = useTable();

  const tablesOnBooking = tables.filter((table) =>
    booking.tableIds?.includes(table.id),
  );

  const seats = tablesOnBooking.reduce((sum, table) => sum + table.maxSeats, 0);

  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
      }
    : undefined;

  const height = (booking.tableIds?.length ?? 1) * 30;

  const matchedShift = getMatchedShifts(shifts, selectedDate).at(0);

  const gap = matchedShift?.gap ?? 0;

  const minutesWidth =
    differenceInMinutes(booking.endTime, booking.startTime) * 2;

  const totalWidth = minutesWidth + gap * 2;

  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      {...listeners}
      {...attributes}
      ref={(el) => {
        setNodeRef(el);
      }}
      style={{
        ...style,
        left: posX,
        width: differenceInMinutes(booking.endTime, booking.startTime) * 2 + 60,
        height: `${height - 4}px`,
        zIndex: 100 + booking.startTime.getHours(),
      }}
      className={cn(
        "absolute top-[2px] z-[95] flex cursor-pointer whitespace-nowrap text-white",
      )}
    >
      <div className="relative w-full h-full">
        <svg className="h-full w-full">
          <defs>
            <pattern
              id="diagonalHatch"
              x="0"
              y="0"
              width="8"
              height="8"
              patternUnits="userSpaceOnUse"
              patternTransform="rotate(45)"
            >
              <rect x="0" y="0" width="8" height="8" fill="#666668"></rect>
              <rect x="0" y="0" width="4" height="8" fill="#48484a"></rect>
            </pattern>
          </defs>
          {(() => {
            const arrowHeight = height - 4;
            const halfHeight = arrowHeight / 2;
            const backgroundVisible = gap > 0;
            const arrowInset = booking.groupSize === 0 ? 0 : 15;

            // Background arrow (full-width) - shown only if gap > 0
            const backgroundPath = `
    M6 0
    H${totalWidth - arrowInset} 
    L${totalWidth} ${halfHeight} 
    L${totalWidth - arrowInset} ${arrowHeight}
    H6
    a4,4 0,0,1 -4,-4
    v-${arrowHeight - 8}
    a4,4 0,0,1 4,-4 Z
  `;

            const mainArrowPath =
              gap > 0
                ? `
      M6 0
      H${totalWidth - gap * 3} 
      L${totalWidth - gap * 2} ${halfHeight} 
      L${totalWidth - gap * 3} ${arrowHeight}
      H6
      Z
    `
                : `
      M6 0
      H${totalWidth - arrowInset} 
      L${totalWidth} ${halfHeight} 
      L${totalWidth - arrowInset} ${arrowHeight}
      H6
      Z
    `;

            const leftRectPath = `
    M6 0
    H30
    V${arrowHeight}
    H6
    a4,4 0,0,1 -4,-4
    v-${arrowHeight - 8}
    a4,4 0,0,1 4,-4 Z
  `;

            return (
              <>
                {backgroundVisible && booking.groupSize > 0 && (
                  <path
                    className={cn(
                      booking.bookingStatus === BookingStatus.active &&
                        "fill-blue-500/80 stroke-blue-800",
                      booking.bookingStatus === BookingStatus.checked_in &&
                        "fill-green-500/80 stroke-green-800",
                      !booking.id && "fill-yellow-500",
                      isOverlapping && "fill-red-500/80 stroke-red-800",
                      booking.groupSize === 0 &&
                        "fill-gray-700 stroke-gray-800",
                    )}
                    d={backgroundPath}
                    opacity="1"
                    strokeWidth="1"
                  />
                )}

                <path
                  style={{
                    ...(booking.groupSize === 0 && {
                      fill: "url(#diagonalHatch)",
                    }),
                  }}
                  className={cn(
                    booking.bookingStatus === BookingStatus.active &&
                      selectedBooking?.id !== booking.id &&
                      "fill-blue-500 stroke-blue-800",
                    booking.bookingStatus === BookingStatus.active &&
                      selectedBooking?.id === booking.id &&
                      "fill-blue-700 stroke-blue-900",
                    booking.bookingStatus === BookingStatus.checked_in &&
                      selectedBooking?.id !== booking.id &&
                      "fill-green-500 stroke-green-800",
                    booking.bookingStatus === BookingStatus.checked_in &&
                      selectedBooking?.id === booking.id &&
                      "fill-green-700 stroke-green-900",
                    !booking.id && "fill-yellow-500",
                    isOverlapping && "fill-red-500/80 stroke-red-800",
                    booking.groupSize === 0 && "stroke-gray-800",
                  )}
                  d={mainArrowPath}
                  opacity="1"
                  strokeWidth="1"
                />

                {booking.groupSize > 0 && (
                  <path
                    className={cn(
                      booking.bookingStatus === BookingStatus.active &&
                        selectedBooking?.id !== booking.id &&
                        "fill-blue-700 stroke-blue-800",
                      booking.bookingStatus === BookingStatus.active &&
                        selectedBooking?.id === booking.id &&
                        "fill-blue-800 stroke-blue-900",
                      booking.bookingStatus === BookingStatus.checked_in &&
                        selectedBooking?.id !== booking.id &&
                        "fill-green-700 stroke-green-800",
                      booking.bookingStatus === BookingStatus.checked_in &&
                        selectedBooking?.id === booking.id &&
                        "fill-green-800 stroke-green-900",
                      !booking.id && "fill-yellow-500",
                      isOverlapping && "fill-red-700 stroke-red-800",
                    )}
                    d={leftRectPath}
                    strokeWidth="1"
                  />
                )}
              </>
            );
          })()}
        </svg>
        <div
          style={{
            width: totalWidth - gap * 3,
          }}
          className="absolute inset-0 flex flex-row items-center"
        >
          {booking.groupSize > 0 && (
            <div className="min-w-[30px] flex items-center justify-center">
              {booking.groupSize}
            </div>
          )}

          <div
            className={cn(
              "w-[calc(100%-4rem)] flex flex-row gap-2 overflow-hidden",
              booking.groupSize === 0 && "w-full",
            )}
          >
            <Tooltip>
              <TooltipTrigger asChild>
                <div
                  className={cn(
                    "flex flex-row gap-1 justify-between items-center w-full overflow-hidden",
                    booking.groupSize === 0 && "text-center justify-center",
                  )}
                >
                  <div className="flex flex-row gap-2 px-2 items-center overflow-hidden">
                    {booking.starred && (
                      <Star className="fill-white min-h-4 min-w-4 max-h-4 max-w-4" />
                    )}
                    {booking.groupSize === 0 && (
                      <div className="text-sm flex-grow truncate">
                        {booking.memo}
                      </div>
                    )}
                    {booking.groupSize > 0 && (
                      <div className="text-sm flex-grow truncate">
                        {booking.firstName
                          ? `${booking.firstName} ${booking.lastName}`
                          : "Walk in"}
                      </div>
                    )}
                  </div>

                  {booking.labels && booking.labels.length > 0 && (
                    <div className="flex flex-row gap-1">
                      {booking.labels.map((label) => (
                        <LabelIcon
                          key={label.id}
                          className="h-5 w-5"
                          color={label.color}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </TooltipTrigger>
              <TooltipContent
                className={cn(
                  "min-w-[200px]",
                  booking.groupSize === 0 && "hidden",
                )}
              >
                <div className="flex flex-col gap-2 text-[12px]">
                  <ul className="flex flex-col font-bold">
                    <li>
                      {t("Table.table_x", {
                        x: tables
                          .filter((table) =>
                            booking.tableIds?.includes(table.id),
                          )
                          .sort((a, b) => a.position - b.position)
                          .map((table) => table.reference)
                          .join(", "),
                      })}
                    </li>
                    <li>
                      {t("Booking.group_name_group_size", {
                        group_name: booking.firstName ?? "Walk in",
                        group_size: booking.groupSize,
                      })}
                    </li>
                    <li>{`${format(booking.startTime, "p")} - ${format(booking.endTime, "p")}`}</li>
                  </ul>
                  {(booking.labels || booking.comment || booking.memo) && (
                    <ul className="border-t pt-2 flex flex-col gap-1">
                      {booking.comment && <li>{`"${booking.comment}"`}</li>}
                      {booking.memo && <li>{`"${booking.memo}"`}</li>}
                      {booking.labels && booking.labels.length > 0 && (
                        <Fragment>
                          {booking.labels.map((label) => (
                            <li
                              className="rounded px-1 border"
                              style={{
                                backgroundColor: label.color,
                                color: getTextColor(label.color),
                              }}
                              key={label.id}
                            >
                              {label.text}
                            </li>
                          ))}
                        </Fragment>
                      )}
                    </ul>
                  )}
                </div>
              </TooltipContent>
            </Tooltip>

            {seats < booking.groupSize && (
              <Tooltip>
                <TooltipTrigger>
                  <AlertCircle color="#ef4444" size={18} />
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t("Booking.group_size_warning")}</p>
                </TooltipContent>
              </Tooltip>
            )}
            {isOverlapping && (
              <Tooltip>
                <TooltipTrigger>
                  <AlertCircle size={18} />
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t("Booking.overlapping_warning")}</p>
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function TableScrollBar({
  scrollContainerRef,
}: {
  scrollContainerRef: React.RefObject<HTMLDivElement>;
}) {
  const dayStart = set(new Date(), {
    hours: 6,
    minutes: 0,
    seconds: 0,
    milliseconds: 0,
  });

  const dayEnd = addDays(
    set(new Date(), {
      hours: 3,
      minutes: 0,
      seconds: 0,
      milliseconds: 0,
    }),
    1,
  );

  const scrollThumbRef = useRef<HTMLDivElement>(null);
  const customScrollBarRef = useRef<HTMLDivElement>(null);
  const animationFrameId = useRef<number | null>(null);
  const [startTime, setStartTime] = useState(dayStart);
  const [endTime, setEndTime] = useState(dayEnd);

  const calculateThumb = useCallback(() => {
    if (scrollContainerRef.current && customScrollBarRef.current) {
      const scrollContainer = scrollContainerRef.current;
      const customScrollBar = customScrollBarRef.current;

      const { clientWidth, scrollWidth, scrollLeft } = scrollContainer;

      const visibleRatio = clientWidth / scrollWidth;

      const thumbWidth = Math.max(
        visibleRatio * customScrollBar.clientWidth,
        20,
      ); // Minimum width

      const maxThumbPosition = customScrollBar.clientWidth - thumbWidth;
      const thumbPosition =
        (scrollLeft / (scrollWidth - clientWidth)) * maxThumbPosition || 0;

      // Apply styles directly for better performance
      if (scrollThumbRef.current) {
        scrollThumbRef.current.style.width = `${thumbWidth}px`;
        scrollThumbRef.current.style.transform = `translateX(${thumbPosition}px)`;
      }
    }
  }, [scrollContainerRef]);

  const handleResize = useCallback(() => {
    if (animationFrameId.current !== null) {
      cancelAnimationFrame(animationFrameId.current);
    }
    animationFrameId.current = requestAnimationFrame(calculateThumb);
  }, [calculateThumb]);

  const handleScroll = useCallback(() => {
    if (animationFrameId.current !== null) {
      cancelAnimationFrame(animationFrameId.current);
    }

    if (scrollContainerRef.current) {
      const scrollLeft = scrollContainerRef.current.scrollLeft;
      const clientWidth = scrollContainerRef.current.clientWidth;

      // Calculate the start X position relative to the timeline
      const startX = scrollLeft;

      // Assuming each 2 pixels corresponds to 1 minute (adjust as needed)
      const minutesFromStart = Math.floor(startX / 2);

      // Calculate the total minutes from the day start time
      const totalMinutesStart =
        dayStart.getHours() * 60 + dayStart.getMinutes() + minutesFromStart;

      // Update the start time
      setStartTime(
        set(dayStart, {
          hours: Math.floor(totalMinutesStart / 60) % 24, // Ensures hours wrap around 24
          minutes: totalMinutesStart % 60,
        }),
      );

      // Calculate the end X position by adding the visible width
      const endX = startX + clientWidth;
      const minutesFromStartEnd = Math.floor(endX / 2);

      // Calculate the total minutes for the end time
      const totalMinutesEnd =
        dayStart.getHours() * 60 + dayStart.getMinutes() + minutesFromStartEnd;

      // Update the end time
      setEndTime(
        set(dayStart, {
          hours: Math.floor(totalMinutesEnd / 60) % 24, // Ensures hours wrap around 24
          minutes: totalMinutesEnd % 60,
        }),
      );
    }

    // Schedule the next frame for thumb calculation
    animationFrameId.current = requestAnimationFrame(calculateThumb);
  }, [calculateThumb, dayStart, scrollContainerRef]);

  useEffect(() => {
    calculateThumb();

    window.addEventListener("resize", handleResize);

    if (scrollContainerRef.current) {
      scrollContainerRef.current.addEventListener("scroll", handleScroll);
    }

    return () => {
      window.removeEventListener("resize", handleResize);
      if (scrollContainerRef.current) {
        scrollContainerRef.current.removeEventListener("scroll", handleScroll);
      }
      if (animationFrameId.current !== null) {
        cancelAnimationFrame(animationFrameId.current);
      }
    };
  }, [calculateThumb, handleResize, handleScroll, scrollContainerRef]);

  // Handle dragging of the scroll thumb
  const isDragging = useRef(false);
  const startX = useRef(0);
  const startScrollLeft = useRef(0);

  const handlePointerDown = (e: React.PointerEvent) => {
    e.preventDefault();

    isDragging.current = true;
    startX.current = e.clientX;
    if (scrollContainerRef.current) {
      startScrollLeft.current = scrollContainerRef.current.scrollLeft;
    }
    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
  };

  const handlePointerMove = (e: PointerEvent) => {
    if (
      !isDragging.current ||
      !scrollContainerRef.current ||
      !customScrollBarRef.current
    )
      return;

    const deltaX = e.clientX - startX.current;

    const customScrollBar = customScrollBarRef.current;

    const thumbWidth = scrollThumbRef.current?.clientWidth ?? 20;

    const maxThumbPosition = customScrollBar.clientWidth - thumbWidth;

    const scrollRatio =
      (scrollContainerRef.current.scrollWidth -
        scrollContainerRef.current.clientWidth) /
      maxThumbPosition;

    scrollContainerRef.current.scrollLeft =
      startScrollLeft.current + deltaX * scrollRatio;
  };

  const handlePointerUp = () => {
    isDragging.current = false;
    window.removeEventListener("pointermove", handlePointerMove);
    window.removeEventListener("pointerup", handlePointerUp);
  };

  return (
    <div className="relative h-7 bg-background w-full border-t border-black">
      {/* Custom Scrollbar */}
      <div
        ref={customScrollBarRef}
        className="absolute top-0 left-0 h-full w-full bg-bunker-800"
      >
        {/* Scroll Thumb */}
        <div
          onPointerDown={handlePointerDown} // Start the drag
          ref={scrollThumbRef}
          className="h-full bg-background flex items-center justify-between px-4 rounded-full cursor-grab"
        >
          <span className="rounded-full px-1 bg-blue-400 text-xs h-4 flex items-center font-mono">
            {format(startTime, "p")}
          </span>
          <span className="rounded-full px-1 bg-blue-400 text-xs h-4 flex items-center font-mono">
            {format(endTime, "p")}
          </span>
        </div>
      </div>
    </div>
  );
}

function TableRow({
  table,
  bookings,
  className,
  timeslots,
}: {
  table: Table;
  bookings: Booking[];
  className?: string;
  timeslots: {
    time: Date;
    capacity: number;
    spots: number;
    interval: number;
  }[];
}) {
  const timelineRef = useRef<HTMLDivElement | null>(null);
  const { openBookingDialog, createTempBooking } = useBookingManager();

  const { bookingDialogOpen, tempBooking, selectedDate, setTempBooking } =
    useTempBookingStore(
      useShallow((state) => ({
        bookingDialogOpen: state.bookingDialogOpen,
        tempBooking: state.tempBooking,
        setTempBooking: state.setTempBooking,
        selectedDate: state.selectedDate,
      })),
    );

  const dayStart = set(selectedDate, {
    hours: 7,
    minutes: 0,
    seconds: 0,
    milliseconds: 0,
  });

  const { setNodeRef } = useDroppable({
    id: table.id,
  });

  const handleTempBooking = async (booking: PartialBooking) => {
    await createTempBooking(booking);
    void openBookingDialog(booking);
  };

  useEffect(() => {
    if (!bookingDialogOpen) {
      setTempBooking(undefined);
    }
  }, [bookingDialogOpen, setTempBooking]);

  return (
    <>
      <div
        role="none"
        onClick={(e) => {
          if (!timelineRef.current) return;

          const { left } = timelineRef.current.getBoundingClientRect();
          const x = e.clientX - left;
          const minutesFromStart = Math.floor(x / 2);
          const totalMinutes = dayStart.getMinutes() + minutesFromStart;

          const bookingStartTime = nearestToMinute(
            set(selectedDate, {
              hours: dayStart.getHours(),
              minutes: totalMinutes,
            }),
            15,
            "floor",
          );

          const booking: PartialBooking = {
            bookingStatus: BookingStatus.active,
            startTime: bookingStartTime,
            endTime: addMinutes(bookingStartTime, DEFAULT_BOOKING_DURATION),
            tableIds: [table.id],
            groupSize: table.maxSeats,
          };

          void handleTempBooking(booking);
        }}
        ref={(el) => {
          timelineRef.current = el;
          setNodeRef(el);
        }}
        className={cn(
          "timeline-row relative flex h-full w-full items-center",
          className,
        )}
      >
        {timeslots.map((timeslot, index) => {
          if (timeslot.capacity === 0) return null;

          const intervalStartMinutes = differenceInMinutes(
            timeslot.time,
            dayStart,
          );

          const intervalEndMinutes = differenceInMinutes(
            addMinutes(timeslot.time, timeslot.interval),
            dayStart,
          );

          const posX = intervalStartMinutes * 2;
          const width = (intervalEndMinutes - intervalStartMinutes) * 2;
          const adjustedPosX = Math.max(posX, 0);

          return (
            <div
              key={index}
              className={cn(
                "absolute top-0 h-full bg-background pointer-events-none",
                timeslot.spots === 0 &&
                  timeslot.capacity > 0 &&
                  "bg-background",
              )}
              style={{
                left: `${adjustedPosX}px`,
                width: `${width}px`,
              }}
            />
          );
        })}

        {tempBooking?.tableIds?.includes(table.id) && (
          <TableBooking
            onClick={() => {}}
            booking={tempBooking}
            posX={
              differenceInMinutes(
                tempBooking.startTime,
                set(dayStart, {
                  date: tempBooking.startTime.getDate(),
                  month: tempBooking.startTime.getMonth(),
                  year: tempBooking.startTime.getFullYear(),
                }),
              ) * 2
            }
          />
        )}

        {bookings.map((booking) => (
          <TableBooking
            onClick={() => {
              void openBookingDialog(booking);
            }}
            key={booking.id}
            booking={booking}
            posX={
              differenceInMinutes(
                booking.startTime,
                set(dayStart, {
                  date: booking.startTime.getDate(),
                  month: booking.startTime.getMonth(),
                  year: booking.startTime.getFullYear(),
                }),
              ) * 2
            }
          />
        ))}
      </div>
    </>
  );
}

function TableRows({
  tables,
  timeslots,
}: {
  tables: Table[];
  timeslots: {
    time: Date;
    capacity: number;
    spots: number;
    interval: number;
  }[];
}) {
  const { bookings } = useBooking();
  const { selectedDate } = useTempBookingStore();

  const sortedTables = tables.sort((a, b) => a.position - b.position);
  const { openTableDialog } = useTableManager();
  return (
    <div className="flex flex-col border">
      {sortedTables.map((table) => {
        const tableBookings = bookings.filter(
          (booking) =>
            isSameDay(booking.startTime, selectedDate) &&
            booking.tableIds.includes(table.id) &&
            booking.status === Status.active &&
            booking.bookingStatus !== BookingStatus.canceled &&
            booking.bookingStatus !== BookingStatus.no_show &&
            booking.bookingStatus !== BookingStatus.checked_out,
        );

        return (
          <div
            className={cn(
              "relative group flex h-[30px] bg-background-tertiary text-black",
            )}
            key={table.id}
          >
            <button
              onClick={() => {
                void openTableDialog(table);
              }}
              className="sticky text-start overflow-hidden flex-shrink-0 top-0 z-[295] left-0 flex h-full w-[110px] items-center justify-start group-last:border-b-0 border-b border-r dark:bg-black bg-white dark:text-white text-black"
            >
              <div className="relative px-2 flex w-full justify-between items-center gap-2">
                <div className="relative flex-1">
                  <p className="truncate font-bold">{`T${table.reference}`}</p>
                  <div
                    className={cn(
                      "h-1 w-1 rounded-full bg-green-500 absolute -top-0 -left-1.5",
                      !table.bookable && "bg-transparent",
                    )}
                  ></div>
                </div>

                <p className="text-xs">{`${table.minSeats} - ${table.maxSeats}`}</p>
              </div>
            </button>
            <TableRow
              timeslots={timeslots}
              className="border-b group-last:border-b-0"
              table={table}
              bookings={tableBookings.filter((booking) => {
                const bookingTablePositions = booking.tableIds
                  .filter((id): id is string => !!id)
                  .map((tableId) => {
                    const foundTable = sortedTables.find(
                      (t) => t.id === tableId,
                    );
                    return foundTable?.position ?? -1;
                  })
                  .sort((a, b) => a - b);

                const firstTablePosition = bookingTablePositions[0];

                return table.position === firstTablePosition;
              })}
            />
          </div>
        );
      })}
    </div>
  );
}

function TimeIndicator({
  scrollContainerRef,
}: {
  scrollContainerRef: React.RefObject<HTMLDivElement>;
}) {
  const [time, setTime] = useState(new Date());
  const indicatorRef = useRef<HTMLDivElement>(null);
  const { selectedDate } = useTempBookingStore();

  const start = set(selectedDate, {
    hours: 7,
    minutes: 0,
    seconds: 0,
    milliseconds: 0,
  });

  useEffect(() => {
    const intervalId = setInterval(() => {
      setTime(new Date());
    }, 5000);

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (indicatorRef.current && scrollContainerRef.current) {
      const indicatorPosition =
        indicatorRef.current.getBoundingClientRect().left;

      scrollContainerRef.current.scrollTo({
        left: indicatorPosition * 0.8,
        behavior: "smooth",
      });
    }
  }, [scrollContainerRef, selectedDate]);
  return (
    <div
      ref={indicatorRef}
      style={{
        left:
          (differenceInSeconds(
            time,
            set(start, {
              date: time.getDate(),
              month: time.getMonth(),
              year: time.getFullYear(),
            }),
          ) /
            60) *
            2 +
          120,
      }}
      className="w-[4px] absolute h-full bg-red-500 z-[300] top-0"
    ></div>
  );
}

function Timeslots({
  amountOfTimeslots,
  shifts,
  dayStart,
}: {
  amountOfTimeslots: number;
  shifts: Shift[];
  dayStart: Date;
}) {
  const { openBookingDialog, createTempBooking } = useBookingManager();

  const handleBooking = (bookingStartTime: Date) => {
    const booking: PartialBooking = {
      bookingStatus: BookingStatus.active,
      startTime: bookingStartTime,
      endTime: addMinutes(bookingStartTime, DEFAULT_BOOKING_DURATION),
      groupSize: 2,
      tableIds: [],
    };
    void createTempBooking(booking);
    void openBookingDialog(booking);
  };

  return (
    <div className="flex z-30 whitespace-nowrap border-b border-black dark:border-border h-8 w-full">
      <div className="bg-background-tertiary text-neutral-600 h-full w-[110px]"></div>
      {Array.from({ length: amountOfTimeslots + 1 }).map((_, idx) => {
        const currentTime = addMinutes(dayStart, idx * 60);
        const isOpen = shifts.find((shift) => shift.closed === true)
          ? false
          : shifts.some((shift) => {
              const shiftStart = set(dayStart, {
                hours: shift.startTime.getHours(),
                minutes: shift.startTime.getMinutes(),
                seconds: 0,
                milliseconds: 0,
              });

              const shiftEnd = addMinutes(
                set(dayStart, {
                  hours: shift.endTime.getHours(),
                  minutes: shift.endTime.getMinutes(),
                  seconds: 0,
                  milliseconds: 0,
                }),
                shift.standardDuration,
              );

              const result = isBetween(currentTime, shiftStart, shiftEnd);

              return result;
            });

        return (
          <Fragment key={idx}>
            <Button
              onClick={() => {
                handleBooking(currentTime);
              }}
              variant="ghost"
              className={cn(
                "timeline-slot rounded-none group before:shadow-light-line before:h-[20%] before:w-[1px] before:bottom-0 before:absolute before:left-0 relative text-sm bg-background-tertiary text-neutral-600 h-full w-[60px] flex items-center justify-center",
                isOpen && "bg-background",
              )}
            >
              <div className="absolute transition-opacity flex inset-0 opacity-0 group-hover:opacity-100 justify-center items-center">
                +
              </div>
              <span
                className={cn(
                  "absolute left-0 top-0 -translate-x-1/2",
                  isOpen && "font-bold text-foreground",
                )}
              >
                {format(currentTime, "HH:mm")}
              </span>
            </Button>
            <Button
              onClick={() => {
                handleBooking(addMinutes(currentTime, 30));
              }}
              variant="ghost"
              className={cn(
                "timeline-slot group rounded-none before:shadow-line dark:before:shadow-light-line before:h-[10%] before:w-[1px] before:bottom-0 before:absolute before:left-0 relative text-sm bg-background-tertiary text-neutral-600 h-full w-[60px] flex items-center justify-center",
                isOpen && "bg-background",
              )}
            >
              <div className="absolute transition-opacity flex inset-0 opacity-0 group-hover:opacity-100 justify-center items-center">
                +
              </div>
            </Button>
          </Fragment>
        );
      })}
    </div>
  );
}

function GuestCount({
  bookings,
  shifts,
  timeslots,
}: {
  bookings: Booking[];
  shifts: Shift[];
  timeslots: {
    time: Date;
    capacity: number;
    spots: number;
    interval: number;
  }[];
  dayStart: Date;
  amountOfTimeslots: number;
}) {
  const { t } = useTranslation("common");
  const capacity = shifts.reduce(
    (acc, curr) => Math.max(acc, curr.capacity),
    0,
  );

  return (
    <div className="z-30 bg-background-tertiary flex flex-row border-b w-full">
      <div className="dark:bg-black bg-neutral-200 sticky left-0 z-30 border-r text-neutral-600 dark:text-white h-full min-w-[110px] max-w-[110px] gap-1 flex items-center justify-center text-xs">
        {t("Booking.guests")}
        <ArrowRightIcon size={15} />
      </div>
      {timeslots.map((timeslot, idx) => {
        const guestsAtTime = bookings
          .filter((booking) => {
            const endTime = set(booking.endTime, {
              hours: booking.endTime.getHours(),
              minutes: booking.endTime.getMinutes() - 1,
              seconds: 0,
              milliseconds: 0,
            });
            return (
              isBetween(timeslot.time, booking.startTime, endTime) &&
              booking.bookingStatus !== BookingStatus.canceled &&
              booking.bookingStatus !== BookingStatus.no_show &&
              booking.bookingStatus !== BookingStatus.checked_out
            );
          })
          .reduce((acc, booking) => acc + booking.groupSize, 0);
        return (
          <Fragment key={idx}>
            <div className="relative odd:before:shadow-line dark:odd:before:shadow-light-line dark:even:before:shadow-light-line even:before:shadow-dark-line before:h-[100%] before:w-[1px] before:bottom-0 before:absolute before:left-0 h-6 min-w-[30px] flex items-center justify-center">
              <span
                className={cn(
                  "text-sm",
                  timeslot.capacity > 0 && "text-green-500",
                  guestsAtTime > 0 &&
                    timeslot.capacity - guestsAtTime < (capacity / 100) * 10 &&
                    "text-orange-500",
                  guestsAtTime > 0 &&
                    timeslot.capacity - guestsAtTime <= 0 &&
                    "text-red-500 font-bold",
                )}
              >
                {timeslot.capacity === 0 && guestsAtTime === 0
                  ? "-"
                  : guestsAtTime}
              </span>
            </div>
          </Fragment>
        );
      })}
    </div>
  );
}

function TablePlanning() {
  const { bookings } = useBooking();

  const { selectedDate } = useTempBookingStore();

  const { shifts } = useShift();

  const isMobile = useMediaQuery(768);

  const { tables } = useTable();

  const { sections } = useSection();

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const { moveBooking } = useBookingManager();

  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      delay: 100,
      distance: 5,
      tolerance: 100,
    },
  });

  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: {
      delay: 100,
      distance: 5,
      tolerance: 0,
    },
  });

  const keyboardSensor = useSensor(KeyboardSensor);

  const sensors = useSensors(mouseSensor, touchSensor, keyboardSensor);

  const matchedShifts = getMatchedShifts(shifts, selectedDate);

  const timeslots = generateTimeslots(matchedShifts, selectedDate).sort(
    (a, b) => a.time.getTime() - b.time.getTime(),
  );

  const dayStart = set(selectedDate, {
    hours: 7,
    minutes: 0,
    seconds: 0,
    milliseconds: 0,
  });

  const dayEnd = addDays(
    set(selectedDate, {
      hours: 2,
      minutes: 0,
      seconds: 0,
      milliseconds: 0,
    }),
    1,
  );

  const timeBetween = differenceInMinutes(dayEnd, dayStart);

  const amountOfTimeslots = timeBetween / 60;

  const handleDragEnd = (event: DragEndEvent) => {
    const booking = bookings.find((b) => b.id === event.active.id);
    const targetTable = tables.find((t) => t.id === event.over?.id);

    if (!booking || !targetTable) {
      console.log("Booking or target table not found.");
      return;
    }

    const requiredSeats = booking.groupSize;
    let selectedTableIds = [targetTable.id];
    let accumulatedSeats = targetTable.maxSeats;

    if (accumulatedSeats >= requiredSeats) {
      // If the target table alone can accommodate the booking
      void moveBooking({ id: booking.id, tableIds: selectedTableIds });
      return;
    }

    const remainingSeats = requiredSeats - accumulatedSeats;

    // Identify neighboring tables by position (adjacent)
    const currentTableRank = targetTable.position;
    const neighboringTables = tables
      .filter((table) => table.locationId === targetTable.locationId)
      .filter(
        (t) =>
          (t.position === currentTableRank - 1 ||
            t.position === currentTableRank + 1) &&
          t.id !== targetTable.id,
      );

    if (neighboringTables.length === 0) {
      void moveBooking({ id: booking.id, tableIds: selectedTableIds });
      return;
    }

    // Get valid combinations that meet or exceed the remaining seats
    const validCombinations = getValidCombinations(
      neighboringTables,
      remainingSeats,
    );

    if (validCombinations.length === 0) {
      void moveBooking({ id: booking.id, tableIds: selectedTableIds });
      return;
    }

    // Sort combinations to find the one with minimal total seats and fewer tables
    validCombinations.sort((a, b) => {
      const seatsA = a.reduce((sum, table) => sum + table.maxSeats, 0);
      const seatsB = b.reduce((sum, table) => sum + table.maxSeats, 0);
      return seatsA !== seatsB ? seatsA - seatsB : a.length - b.length;
    });

    const optimalCombination = validCombinations[0];
    const optimalCombinationIds = optimalCombination.map((t) => t.id);

    selectedTableIds = [targetTable.id, ...optimalCombinationIds];
    accumulatedSeats += optimalCombination.reduce(
      (sum, table) => sum + table.maxSeats,
      0,
    );

    // Move the booking if we have sufficient seats
    if (accumulatedSeats >= requiredSeats) {
      void moveBooking({ id: booking.id, tableIds: selectedTableIds });
    } else {
      console.log("Not enough seats available in neighboring tables.");
    }
  };

  return (
    <div className="flex flex-col overflow-auto w-full flex-1">
      <DndContext
        autoScroll={{
          threshold: {
            x: 0,
            y: 0.2,
          },
        }}
        sensors={sensors}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToVerticalAxis]}
      >
        <div
          ref={scrollContainerRef}
          className={cn(
            "flex flex-1 flex-col overflow-x-auto hide-scrollbar overscroll-none",
          )}
        >
          <div className="flex flex-col w-[2520px] sticky top-0 z-[299]">
            <Timeslots
              dayStart={dayStart}
              amountOfTimeslots={amountOfTimeslots}
              shifts={matchedShifts}
            />
            <GuestCount
              bookings={bookings}
              shifts={matchedShifts}
              timeslots={timeslots}
              dayStart={dayStart}
              amountOfTimeslots={amountOfTimeslots}
            />
          </div>

          <div className="relative w-[2520px] select-none flex flex-col">
            {isSameDay(selectedDate, new Date()) && (
              <TimeIndicator scrollContainerRef={scrollContainerRef} />
            )}
            {sections
              .sort((a, b) => a.position - b.position)
              .map((section) => {
                const tablesInSection = tables.filter(
                  (table) =>
                    table.locationId === section.id && table.minSeats > 0,
                );

                if (tablesInSection.length === 0) return null;
                return (
                  <div key={section.id}>
                    <div className="sticky w-full flex top-0 left-0 z-[298] bg-white dark:bg-[#050505] dark:text-white p-1 text-black">
                      <div className="left-0 w-max pl-4 sticky">
                        <span className="text-sm font-bold">
                          {section.name}
                        </span>
                      </div>
                    </div>

                    <TableRows timeslots={timeslots} tables={tablesInSection} />
                  </div>
                );
              })}
          </div>
        </div>
        {!isMobile && (
          <TableScrollBar scrollContainerRef={scrollContainerRef} />
        )}
      </DndContext>
    </div>
  );
}
